import { t } from '@lingui/macro';
export type BrowserNotificationPermission = NotificationPermission | 'unsupported';

export type BrowserNotificationPreferences = {
  browserEnabled: boolean;
  soundEnabled: boolean;
  throttleEnabled: boolean;
};

export type BrowserNotificationInput = {
  id?: string | null;
  title: string;
  body?: string | null;
  tag?: string | null;
};

export type BrowserNotificationEffectResult = {
  browserShown: boolean;
  soundPlayed: boolean;
  suppressed: boolean;
  permission: BrowserNotificationPermission;
};

const STORAGE_KEY = 'jema.browser-notification-preferences.v1';
const PREFERENCES_EVENT = 'jema-browser-notification-preferences-changed';
const EFFECT_THROTTLE_MS = 9_000;
const MAX_SEEN_IDS = 200;

const defaultPreferences: BrowserNotificationPreferences = {
  browserEnabled: false,
  soundEnabled: true,
  throttleEnabled: true,
};

let lastBrowserNotificationAt = 0;
let lastSoundAt = 0;
let suppressedSinceLastBrowserNotification = 0;
let audioContext: AudioContext | null = null;
const seenEffectIds = new Set<string>();

export const getBrowserNotificationPreferences = (): BrowserNotificationPreferences => {
  if (typeof window === 'undefined') return defaultPreferences;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPreferences;
    const parsed = JSON.parse(raw) as Partial<BrowserNotificationPreferences>;
    return {
      browserEnabled: Boolean(parsed.browserEnabled),
      soundEnabled:
        typeof parsed.soundEnabled === 'boolean'
          ? parsed.soundEnabled
          : defaultPreferences.soundEnabled,
      throttleEnabled:
        typeof parsed.throttleEnabled === 'boolean'
          ? parsed.throttleEnabled
          : defaultPreferences.throttleEnabled,
    };
  } catch {
    return defaultPreferences;
  }
};

export const setBrowserNotificationPreferences = (
  next: Partial<BrowserNotificationPreferences>
) => {
  if (typeof window === 'undefined') return defaultPreferences;
  const preferences = {
    ...getBrowserNotificationPreferences(),
    ...next,
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  window.dispatchEvent(
    new CustomEvent<BrowserNotificationPreferences>(PREFERENCES_EVENT, {
      detail: preferences,
    })
  );
  return preferences;
};

export const subscribeToBrowserNotificationPreferences = (
  listener: (preferences: BrowserNotificationPreferences) => void
) => {
  if (typeof window === 'undefined') return () => undefined;
  const handleChange = () => listener(getBrowserNotificationPreferences());
  window.addEventListener(PREFERENCES_EVENT, handleChange);
  window.addEventListener('storage', handleChange);
  return () => {
    window.removeEventListener(PREFERENCES_EVENT, handleChange);
    window.removeEventListener('storage', handleChange);
  };
};

export const getBrowserNotificationPermission = (): BrowserNotificationPermission => {
  if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
    return 'unsupported';
  }
  return window.Notification.permission;
};

export const requestBrowserNotificationPermission =
  async (): Promise<BrowserNotificationPermission> => {
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
      return 'unsupported';
    }
    const permission =
      window.Notification.permission === 'default'
        ? await window.Notification.requestPermission()
        : window.Notification.permission;

    setBrowserNotificationPreferences({
      browserEnabled: permission === 'granted',
    });
    return permission;
  };

export const notifyWithBrowserEffects = (
  input: BrowserNotificationInput
): BrowserNotificationEffectResult => {
  const permission = getBrowserNotificationPermission();
  const preferences = getBrowserNotificationPreferences();
  const id = input.id?.trim() || null;

  if (id && seenEffectIds.has(id)) {
    return {
      browserShown: false,
      soundPlayed: false,
      suppressed: true,
      permission,
    };
  }
  if (id) rememberEffectId(id);

  if (!preferences.browserEnabled) {
    return {
      browserShown: false,
      soundPlayed: false,
      suppressed: false,
      permission,
    };
  }

  const now = Date.now();
  const browserShown = maybeShowBrowserNotification(input, preferences, permission, now);
  const soundPlayed = maybePlaySound(preferences, now);

  return {
    browserShown,
    soundPlayed,
    suppressed: !browserShown || !soundPlayed,
    permission,
  };
};

export const resetBrowserNotificationEffectsForTests = () => {
  lastBrowserNotificationAt = 0;
  lastSoundAt = 0;
  suppressedSinceLastBrowserNotification = 0;
  audioContext = null;
  seenEffectIds.clear();
};

const maybeShowBrowserNotification = (
  input: BrowserNotificationInput,
  preferences: BrowserNotificationPreferences,
  permission: BrowserNotificationPermission,
  now: number
) => {
  if (
    permission !== 'granted' ||
    typeof window === 'undefined' ||
    typeof window.Notification === 'undefined'
  ) {
    return false;
  }
  if (
    preferences.throttleEnabled &&
    lastBrowserNotificationAt > 0 &&
    now - lastBrowserNotificationAt < EFFECT_THROTTLE_MS
  ) {
    suppressedSinceLastBrowserNotification += 1;
    return false;
  }

  const suppressedCount = suppressedSinceLastBrowserNotification;
  suppressedSinceLastBrowserNotification = 0;
  lastBrowserNotificationAt = now;
  const title = suppressedCount > 0 ? t`New updates received` : input.title;
  const body =
    suppressedCount > 0
      ? t`${suppressedCount + 1} updates arrived recently. Open the workspace to review them.`
      : (input.body ?? undefined);

  new window.Notification(title, {
    body,
    tag: input.tag ?? input.id ?? undefined,
    silent: true,
  });
  return true;
};

const maybePlaySound = (preferences: BrowserNotificationPreferences, now: number) => {
  if (!preferences.soundEnabled) return false;
  if (preferences.throttleEnabled && lastSoundAt > 0 && now - lastSoundAt < EFFECT_THROTTLE_MS) {
    return false;
  }
  lastSoundAt = now;
  playNotificationChime();
  return true;
};

const playNotificationChime = () => {
  if (typeof window === 'undefined') return;
  const AudioContextCtor =
    window.AudioContext ??
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) return;
  audioContext ??= new AudioContextCtor();
  playTone(660, 0);
  window.setTimeout(() => playTone(880, 90), 90);
};

const playTone = (frequency: number, delayMs: number) => {
  if (!audioContext) return;
  const startAt = audioContext.currentTime + delayMs / 1000;
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.value = frequency;
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(0.12, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.22);
  oscillator.connect(gain);
  gain.connect(audioContext.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + 0.24);
};

const rememberEffectId = (id: string) => {
  seenEffectIds.add(id);
  if (seenEffectIds.size <= MAX_SEEN_IDS) return;
  const oldest = seenEffectIds.values().next().value as string | undefined;
  if (oldest) seenEffectIds.delete(oldest);
};
